import React, { useRef, useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { PictureStyle, PictureStyleProps, PictureWrapper } from './Picture.css';

type SourceSet = Array<{
    srcSet: string;
    media: string;
}>;

export type PicturePropType = React.ImgHTMLAttributes<HTMLImageElement> &
    PictureStyleProps & {
        sources?: SourceSet;
        imageError?: () => void;
        imageLoaded?: () => void;
        fallbackSrc?: string;
    };

const isInvalidSrc = (src?: string, srcSet?: SourceSet) => (!src || src === '') && srcSet?.map((x) => x.srcSet).join('') === '';

export const Picture: React.FC<PicturePropType> = ({
    sources = [],
    fit,
    vPosition,
    hPosition,
    src,
    fallbackSrc,
    alwaysLoad = false,
    imageError,
    imageLoaded,
    children,
    alt,
    ...rest
}) => {
    const [ref, inView] = useInView({
        triggerOnce: true,
    });
    const supportsLazyLoading = 'loading' in HTMLImageElement.prototype;

    const imgRef = useRef<HTMLImageElement>(null);
    const [error, setError] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [currentSrc, setCurrentSrc] = useState(src);
    const [triedFallback, setTriedFallback] = useState(false);

    const invalidSrc = isInvalidSrc(src, sources);
    const showFallback = invalidSrc || (error && (triedFallback || !fallbackSrc));

    useEffect(() => {
        setCurrentSrc(src);
        setError(false);
        setTriedFallback(false);
        setLoaded(false);
    }, [src]);

    useEffect(() => {
        if (imgRef.current?.complete && !loaded) {
            setLoaded(true);
        }
    });

    const handleImageError = () => {
        if (!triedFallback && fallbackSrc && currentSrc !== fallbackSrc) {
            setCurrentSrc(fallbackSrc);
            setTriedFallback(true);
            setError(false);
        } else {
            setError(true);
        }
    };

    useEffect(() => {
        if (showFallback) {
            if (imageError) {
                imageError();
            }
        }
    }, [showFallback, imageError]);

    useEffect(() => {
        if (loaded && imageLoaded) {
            imageLoaded();
        }
    }, [loaded, imageLoaded]);

    if (showFallback) {
        return <>{children}</>;
    }

    if (!showFallback && error) {
        return <>{alt}</>;
    }

    return (
        <PictureWrapper data-test-id={'tv-castandcrew-castmemberphoto'} ref={!supportsLazyLoading ? ref : null}>
            <>
                {inView || alwaysLoad || supportsLazyLoading ? (
                    <PictureStyle
                        data-test-id={'tv-detailscreen-channellogo'}
                        onError={handleImageError}
                        onLoad={() => {
                            setLoaded(true);
                        }}
                        fit={fit}
                        vPosition={vPosition}
                        hPosition={hPosition}
                        loaded={loaded}
                    >
                        {sources.map(({ media, srcSet }, k) => (
                            <source key={k} srcSet={srcSet} media={media} />
                        ))}

                        <img ref={imgRef} alt={alt} {...rest} src={src} loading={alwaysLoad ? 'eager' : 'lazy'} />
                    </PictureStyle>
                ) : (
                    children
                )}
            </>
        </PictureWrapper>
    );
};
